<template>
    <div>
        <div v-if="plans.length > 0">
            <!-- Team Status -->
            <div class="mb-4">
                <team-status :team="team" :impersonator="isImpersonatorOrPlatformAdmin"></team-status>
            </div>

            <!-- Trial Expiration Notice -->
            <subscription-notice
                :cashier-path="cashierPath"
                :team-subscription="teamSubscription"
                :team-last-payment-id="teamLastPaymentId"
                :active-subscription="activeSubscription"
                :team="team"
            />

            <!-- New Subscription -->
            <div v-if="needsSubscription && hasImpersonator">
                <subscribe-stripe
                    :team="team"
                    :teams="teams"
                    :plans="plans"
                    :form="form"
                    :cardForm="cardForm"
                    :stripe="stripe"
                    :countriesList="countriesList"
                    :showing-monthly-plans="showingMonthlyPlans"
                    :showing-yearly-plans="showingYearlyPlans"
                    :selected-plan="selectedPlan"
                    :tax-rate="taxRate"
                    @show-monthly-plans="showMonthlyPlans"
                    @show-yearly-plans="showYearlyPlans"
                    @plan-selected="selectPlan"
                    @subscribe="subscribe"
                />
            </div>

            <!-- Update Subscription -->
            <div v-if="subscriptionIsActive && hasImpersonator">
                <update-subscription
                    :team="team"
                    :teams="teams"
                    :plans="plans"
                    :active-subscription="activeSubscription"
                    :showing-monthly-plans="showingMonthlyPlans"
                    :showing-yearly-plans="showingYearlyPlans"
                    @show-monthly-plans="showMonthlyPlans"
                    @show-yearly-plans="showYearlyPlans"
                />
            </div>

            <!-- Resume Subscription -->
            <div v-if="subscriptionIsOnGracePeriod && hasImpersonator">
                <resume-subscription
                    :team="team"
                    :plans="plans"
                    :teams="teams"
                    :active-subscription="activeSubscription"
                    :showing-monthly-plans="showingMonthlyPlans"
                    :showing-yearly-plans="showingYearlyPlans"
                    @show-monthly-plans="showMonthlyPlans"
                    @show-yearly-plans="showYearlyPlans"
                />
            </div>

            <!-- Message Balance -->
            <div class="card card-default" v-show="hasMessageBalance">
                <div class="card-header">Message Balance</div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-9">
                            <p>
                                The current message balance is: <b>{{ messageBalance }}</b>.
                            </p>
                        </div>
                        <div class="col-3 text-right">
                            <vx-button
                                v-if="isImpersonatorOrPlatformAdmin"
                                color="muted"
                                @click="isMessageBalanceModalVisible = true"
                            >Adjust</vx-button>
                        </div>
                    </div>
                    <modal-adjust-message-balance
                        v-if="isImpersonatorOrPlatformAdmin"
                            v-model="messageBalance"
                            v-model:visible="isMessageBalanceModalVisible"
                    >
                    </modal-adjust-message-balance>
                </div>
            </div>

            <!-- Credits used -->
            <div v-if="rechargeAndNoRollover" class="card card-default">
                <div class="card-header">Credits Used</div>
                <div class="card-body">
                    <div class="row m-0">
                        <div class="col p-0">
                            <vx-alert color="info" :closeable="false">
                                These numbers are not real-time. It can take a few hours for usage to show up here.
                            </vx-alert>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-9">
                            <p>
                                Current month to date:
                                <vx-icon-loading v-if="totalCreditsUsedMonth === undefined" spin class="w-3! h-3!" ></vx-icon-loading>
                                <b v-else>{{ totalCreditsUsedMonth || 'N/A' }}</b>
                            </p>
                            <p>
                                Previous month:
                                <vx-icon-loading v-if="totalCreditsUsedPreviousMonth === undefined" spin class="w-3! h-3!" ></vx-icon-loading>
                                <b v-else>{{ totalCreditsUsedPreviousMonth || 'N/A' }}</b>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Cancel Subscription -->
            <div v-if="subscriptionIsActive">
                <cancel-subscription
                    :team="team"
                    :form="form"
                    :impersonator="hasImpersonator"
                    :team-id="teamId"
                ></cancel-subscription>
            </div>
        </div>

    </div>
</template>

<script>
import TeamStatus from '~/components/settings/subscription/TeamStatus.vue';
import SubscriptionNotice from './SubscriptionNotice.vue';
import SubscribeStripe from './SubscribeStripe.vue';
import UpdateSubscription from './UpdateSubscription.vue';
import ResumeSubscription from './ResumeSubscription.vue';
import CancelSubscription from './CancelSubscription.vue';
import { VxButton, VxIconLoading , VxAlert } from '@voxie/frontend-components';
import ModalAdjustMessageBalance from './ModalAdjustMessageBalance.vue';
import { analyticsClient } from '~/services';
import dayjs from '~/utils/dayjs';
import { getCurrentMonthDateRange, getPreviousMonthDateRange } from '~/composables/useBillingDates';
import axios from '~/services/axios';

export default {
    props: [
        'team',
        'teams',
        'isImpersonatorOrPlatformAdmin',
        'cashierPath',
        'teamSubscription',
        'teamLastPaymentId',
        'teamMessageBalance',
        'countriesList',
        'hasImpersonator',
    ],
    components: {
        VxIconLoading,
        TeamStatus,
        SubscriptionNotice,
        SubscribeStripe,
        UpdateSubscription,
        ResumeSubscription,
        CancelSubscription,
        VxButton,
        ModalAdjustMessageBalance,
        VxAlert
    },
    data() {
        return {
            cardElement: null,
            stripe: Spark.stripeKey ? Stripe(Spark.stripeKey, {
                apiVersion: Spark.stripeApiVersion
            }) : null,
            form: new SparkForm({
                use_existing_payment_method: this.hasPaymentMethod() ? '1' : '0',
                stripe_payment_method: '',
                plan: '',
                coupon: null,
                address: '',
                address_line_2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
            }),
            cardForm: new SparkForm({
                name: ''
            }),
            plans: [],
            showingMonthlyPlans: true,
            showingYearlyPlans: false,
            selectedPlan: null,
            taxRate: 0,
            isMessageBalanceModalVisible: false,
            messageBalance: this.teamMessageBalance,
            totalCreditsUsedMonth: undefined,
            totalCreditsUsedPreviousMonth: undefined,
        };
    },
    watch: {
        'currentBillingAddress': function () {
            this.refreshTaxRate(this.form);
        }
    },
    mounted() {
        this.getPlans();
        this.initializeBillingAddress();
        this.loadCurrentMonthCreditsUsed();
        this.loadPreviousMonthCreditsUsed();

    },
    methods: {
        async loadCurrentMonthCreditsUsed() {
            const currentMonthDates = getCurrentMonthDateRange();

            if (!currentMonthDates) {
                this.totalCreditsUsedMonth = null;
                return;
            }

            try {
                const res = await analyticsClient.getUsageTotalByType(
                    { teamId: this.teamId },
                    {
                        range: [currentMonthDates.start_date, currentMonthDates.end_date],
                        filters: {
                            usage_types: ['mms_message', 'sms_segment'],
                        },
                    }
                );

                this.totalCreditsUsedMonth = this.calculateCreditsUsage(res.data?.data);
            } catch (error) {
                this.totalCreditsUsedMonth = null;

                console.error(error);
                this.$toasted.global.platform_error();
            }

        },
        async loadPreviousMonthCreditsUsed() {
            const previousMonthDates = getPreviousMonthDateRange();

            if (!previousMonthDates) {
                this.totalCreditsUsedPreviousMonth = null;
                return;
            }

            try {
                const res = await analyticsClient.getUsageTotalByType(
                    { teamId: this.teamId },
                    {
                        range: [previousMonthDates.start_date, previousMonthDates.end_date],
                        filters: {
                            usage_types: ['mms_message', 'sms_segment'],
                        },
                    }
                );

                this.totalCreditsUsedPreviousMonth = this.calculateCreditsUsage(res.data?.data);
            } catch (error) {
                this.totalCreditsUsedPreviousMonth = null;

                console.error(error);
                this.$toasted.global.platform_error();
            }

        },
        calculateCreditsUsage(data) {
            if (!data || !Array.isArray(data)) {
                return null;
            }

            let amount = 0;
            data.forEach((record) => {
                switch (record[['usage_type']]) {
                    case 'mms_message':
                        amount += record['values'][0] * 2;
                        break;
                    case 'sms_segment':
                        amount += record['values'][0];
                        break;
                }
            });

            return amount;
        },
        refreshTaxRate(form) {
            axios.post('/tax-rate', JSON.parse(JSON.stringify(form)))
                .then(response => {
                    this.taxRate = response.data.rate;
                });
        },
        showMonthlyPlans() {
            this.showingMonthlyPlans = true;

            this.showingYearlyPlans = false;
        },
        showYearlyPlans() {
            this.showingMonthlyPlans = false;

            this.showingYearlyPlans = true;
        },
        hasPaymentMethod() {
            return this.team.card_last_four;
        },
        getPlans() {
            axios.get('/spark/plans')
                .then(response => {
                    this.plans = response.data.filter((billable) => billable.type === 'team')
                });
        },
        initializeBillingAddress() {
            this.form.address = this.team.billing_address;
            this.form.address_line_2 = this.team.billing_address_line_2;
            this.form.city = this.team.billing_city;
            this.form.state = this.team.billing_state;
            this.form.zip = this.team.billing_zip;
            this.form.country = this.team.billing_country || 'US';
        },
        selectPlan(plan) {
            this.selectedPlan = plan;

            this.form.plan = this.selectedPlan.id;
        },
        subscribe() {
            this.cardForm.errors.forget();

            this.form.startProcessing();

            if (this.form.use_existing_payment_method == '1') {
                return this.createSubscription();
            }

            const payload = {
                name: this.cardForm.name,
                address: {
                    line1: this.form.address || '',
                    line2: this.form.address_line_2 || '',
                    city: this.form.city || '',
                    state: this.form.state || '',
                    postal_code: this.form.zip || '',
                    country: this.form.country || '',
                }
            };

            this.generateToken(secret => {
                this.stripe.handleCardSetup(secret, this.cardElement, {
                    payment_method_data: {
                        billing_details: payload
                    }
                }).then(response => {
                    if (response.error) {
                        this.cardForm.errors.set({card: [
                                response.error.message
                            ]});

                        this.form.busy = false;
                    } else {
                        this.createSubscription(response.setupIntent.payment_method);
                    }
                });
            });
        },
        createSubscription(token) {
            this.form.stripe_payment_method = token;

            axios.post(this.urlForNewSubscription, this.form)
                .then(() => {
                    Bus.$emit('updateTeam');
                }).catch(errors => {
                    if (errors.response.status == 400) {
                        window.location.assign('/' + Spark.cashierPath + '/payment/' + errors.response.data.paymentId + '?redirect=' + this.urlForPlanRedirect);
                    } else {
                        this.form.setErrors(errors.response.data.errors);
                    }
                });
        },
        generateToken(callback){
            return axios.get('/stripe/token').then(response => callback(response.data.clientSecret));
        },
    },
    computed: {
        urlForNewSubscription() {
            return `/settings/teams/${this.team.id}/subscription`;
        },
        urlForPlanRedirect() {
            return `/settings/teams/${this.team.id}%23/subscription`;
        },
        currentBillingAddress() {
            return this.form.address +
                this.form.address_line_2 +
                this.form.city +
                this.form.state +
                this.form.zip +
                this.form.country
        },
        needsSubscription() {
            return ! this.activeSubscription ||
                (this.activeSubscription.ends_at &&
                dayjs.utc().isAfter(dayjs.utc(this.activeSubscription.ends_at)));
        },
        subscriptionIsActive() {
            return this.activeSubscription && ! this.activeSubscription.ends_at;
        },
        subscriptionIsOnGracePeriod() {
            return this.activeSubscription &&
                this.activeSubscription.ends_at &&
                dayjs.utc().isBefore(dayjs.utc(this.activeSubscription.ends_at));
        },
        activeSubscription() {
            if (!this.team) {
                return;
            }

            const subscription = this.team.subscriptions?.find(
                (subscription) =>
                    subscription.name === 'default'
                    && !['incomplete', 'incomplete_expired', 'past_due', 'unpaid'].includes(subscription.stripe_status)
            );

            return subscription;
        },
        activePlan() {
            const activePlanId = this.activeSubscription?.provider_plan;

            return this.plans?.find((plan) => {
                return plan.id === activePlanId;
            });
        },
        hasMessageBalance() {
            return (this.activePlan?.attributes?.recharge ?? false) && !(this.activePlan?.attributes?.no_rollover ?? false);
        },
        rechargeAndNoRollover() {
            return (this.activePlan?.attributes?.recharge ?? false) && (this.activePlan?.attributes?.no_rollover ?? false);
        },
        teamId() {
            return Spark.state.currentTeam.id;
        }
    }
};
</script>

<style scoped>
:deep(.card) {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: hsl(203, 22%, 97%);
  background-clip: border-box;
  border: 0 solid rgba(55, 63, 67, 0.125);
  border-radius: 0.25rem;
}
:deep(.card > hr) {
  margin-right: 0;
  margin-left: 0;
}
:deep(.card > .list-group),
:deep(.card > .chat-contact-group) {
  border-top: inherit;
  border-bottom: inherit;
}
:deep(.card > .list-group:first-child),
:deep(.card > .chat-contact-group:first-child) {
  border-top-width: 0;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}
:deep(.card > .list-group:last-child),
:deep(.card > .chat-contact-group:last-child) {
  border-bottom-width: 0;
  border-bottom-right-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}
:deep(.card > .card-header + .list-group),
:deep(.card > .card-header + .chat-contact-group),
:deep(.card > .list-group + .card-footer),
:deep(.card > .chat-contact-group + .card-footer) {
  border-top: 0;
}
:deep(.card-body) {
  flex: 1 1 auto;
  min-height: 1px;
  padding: 1.25rem;
}
:deep(.card-title) {
  margin-bottom: 0.75rem;
}
:deep(.card-subtitle) {
  margin-top: -0.375rem;
  margin-bottom: 0;
}
:deep(.card-text:last-child) {
  margin-bottom: 0;
}
:deep(.card-link:hover) {
  text-decoration: none;
}
:deep(.card-link + .card-link) {
  margin-left: 1.25rem;
}
:deep(.card-header) {
  padding: 0.75rem 1.25rem;
  margin-bottom: 0;
  background-color: #fff;
  border-bottom: 0 solid rgba(55, 63, 67, 0.125);
}
:deep(.card-header:first-child) {
  border-radius: 0.25rem 0.25rem 0 0;
}
:deep(.card-footer) {
  padding: 0.75rem 1.25rem;
  background-color: #fff;
  border-top: 0 solid rgba(55, 63, 67, 0.125);
}
:deep(.card-footer:last-child) {
  border-radius: 0 0 0.25rem 0.25rem;
}
:deep(.card-header-tabs) {
  margin-right: -0.625rem;
  margin-bottom: -0.75rem;
  margin-left: -0.625rem;
  border-bottom: 0;
}
:deep(.card-header-pills) {
  margin-right: -0.625rem;
  margin-left: -0.625rem;
}
:deep(.card-img-overlay) {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 1.25rem;
  border-radius: 0.25rem;
}
:deep(.card-img),
:deep(.card-img-top),
:deep(.card-img-bottom) {
  flex-shrink: 0;
  width: 100%;
}
:deep(.card-img),
:deep(.card-img-top) {
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}
:deep(.card-img),
:deep(.card-img-bottom) {
  border-bottom-right-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}
:deep(.card-deck .card) {
  margin-bottom: 15px;
}
@media (min-width: 576px) {
  :deep(.card-deck) {
    display: flex;
    flex-flow: row wrap;
    margin-right: -15px;
    margin-left: -15px;
  }
  :deep(.card-deck .card) {
    flex: 1 0 0%;
    margin-right: 15px;
    margin-bottom: 0;
    margin-left: 15px;
  }
}
:deep(.card-group > .card) {
  margin-bottom: 15px;
}
@media (min-width: 576px) {
  :deep(.card-group) {
    display: flex;
    flex-flow: row wrap;
  }
  :deep(.card-group > .card) {
    flex: 1 0 0%;
    margin-bottom: 0;
  }
  :deep(.card-group > .card + .card) {
    margin-left: 0;
    border-left: 0;
  }
  :deep(.card-group > .card:not(:last-child)) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  :deep(.card-group > .card:not(:last-child) .card-img-top),
  :deep(.card-group > .card:not(:last-child) .card-header) {
    border-top-right-radius: 0;
  }
  :deep(.card-group > .card:not(:last-child) .card-img-bottom),
  :deep(.card-group > .card:not(:last-child) .card-footer) {
    border-bottom-right-radius: 0;
  }
  :deep(.card-group > .card:not(:first-child)) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  :deep(.card-group > .card:not(:first-child) .card-img-top),
  :deep(.card-group > .card:not(:first-child) .card-header) {
    border-top-left-radius: 0;
  }
  :deep(.card-group > .card:not(:first-child) .card-img-bottom),
  :deep(.card-group > .card:not(:first-child) .card-footer) {
    border-bottom-left-radius: 0;
  }
}
:deep(.card-columns .card) {
  margin-bottom: 0.75rem;
}
@media (min-width: 576px) {
  :deep(.card-columns) {
    -moz-column-count: 3;
    column-count: 3;
    -moz-column-gap: 1.25rem;
    column-gap: 1.25rem;
    orphans: 1;
    widows: 1;
  }
  :deep(.card-columns .card) {
    display: inline-block;
    width: 100%;
  }
}
:deep(.alert) {
  position: relative;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}
:deep(.alert-heading) {
  color: inherit;
}
:deep(.alert-link) {
  font-weight: 700;
}
:deep(.alert-dismissible) {
  padding-right: 4rem;
}
:deep(.alert-dismissible .close) {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  padding: 0.75rem 1.25rem;
  color: inherit;
}
:deep(.alert-primary) {
  color: #3d7293;
  background-color: #d9ecf8;
  border-color: #cae5f5;
}
:deep(.alert-primary hr) {
  border-top-color: #b5daf1;
}
:deep(.alert-primary .alert-link) {
  color: #2e566f;
}
:deep(.alert-secondary) {
  color: #5f6a70;
  background-color: #e6e9eb;
  border-color: #dde0e3;
}
:deep(.alert-secondary hr) {
  border-top-color: #cfd3d7;
}
:deep(.alert-secondary .alert-link) {
  color: #485054;
}
:deep(.alert-success) {
  color: #2f7544;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
:deep(.alert-success hr) {
  border-top-color: #b1dfbb;
}
:deep(.alert-success .alert-link) {
  color: #20512f;
}
:deep(.alert-info) {
  color: #267280;
  background-color: #d1ecf1;
  border-color: #bee5eb;
}
:deep(.alert-info hr) {
  border-top-color: #abdde5;
}
:deep(.alert-info .alert-link) {
  color: #1a4f59;
}
:deep(.alert-warning) {
  color: #9f8324;
  background-color: #fff3cd;
  border-color: #ffeeba;
}
:deep(.alert-warning hr) {
  border-top-color: #ffe8a1;
}
:deep(.alert-warning .alert-link) {
  color: #75611b;
}
:deep(.alert-danger) {
  color: #8d3a44;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}
:deep(.alert-danger hr) {
  border-top-color: #f1b0b7;
}
:deep(.alert-danger .alert-link) {
  color: #692b33;
}
:deep(.alert-light) {
  color: #999ea0;
  background-color: #fdfdfd;
  border-color: #fcfcfc;
}
:deep(.alert-light hr) {
  border-top-color: #efefef;
}
:deep(.alert-light .alert-link) {
  color: #7f8587;
}
:deep(.alert-dark) {
  color: #4a545a;
  background-color: #dee1e2;
  border-color: #d1d5d7;
}
:deep(.alert-dark hr) {
  border-top-color: #c3c9cb;
}
:deep(.alert-dark .alert-link) {
  color: #333a3e;
}

:deep(.table) {
  width: 100%;
  margin-bottom: 1rem;
  color: hsl(203, 10%, 32%);
}
:deep(.table th),
:deep(.table td) {
  padding: 0.75rem;
  vertical-align: top;
  border-top: 1px solid hsl(203, 10%, 80%);
}
:deep(.table thead th) {
  vertical-align: bottom;
  border-bottom: 2px solid hsl(203, 10%, 80%);
}
:deep(.table tbody + tbody) {
  border-top: 2px solid hsl(203, 10%, 80%);
}
:deep(.table-sm th),
:deep(.table-sm td) {
  padding: 0.3rem;
}
:deep(.table-bordered) {
  border: 1px solid hsl(203, 10%, 80%);
}
:deep(.table-bordered th),
:deep(.table-bordered td) {
  border: 1px solid hsl(203, 10%, 80%);
}
:deep(.table-bordered thead th),
:deep(.table-bordered thead td) {
  border-bottom-width: 2px;
}
:deep(.table-borderless th),
:deep(.table-borderless td),
:deep(.table-borderless thead th),
:deep(.table-borderless tbody + tbody) {
  border: 0;
}
:deep(.table-striped tbody tr:nth-of-type(odd)) {
  background-color: rgba(55, 63, 67, 0.05);
}
:deep(.table-hover tbody tr:hover) {
  color: hsl(203, 10%, 32%);
  background-color: rgba(55, 63, 67, 0.075);
}
:deep(.table-primary),
:deep(.table-primary > th),
:deep(.table-primary > td) {
  background-color: #cae5f5;
}
:deep(.table-primary th),
:deep(.table-primary td),
:deep(.table-primary thead th),
:deep(.table-primary tbody + tbody) {
  border-color: #9dcfed;
}
:deep(.table-hover .table-primary:hover) {
  background-color: #b5daf1;
}
:deep(.table-hover .table-primary:hover > td),
:deep(.table-hover .table-primary:hover > th) {
  background-color: #b5daf1;
}
:deep(.table-secondary),
:deep(.table-secondary > th),
:deep(.table-secondary > td) {
  background-color: #dde0e3;
}
:deep(.table-secondary th),
:deep(.table-secondary td),
:deep(.table-secondary thead th),
:deep(.table-secondary tbody + tbody) {
  border-color: #bfc6ca;
}
:deep(.table-hover .table-secondary:hover) {
  background-color: #cfd3d7;
}
:deep(.table-hover .table-secondary:hover > td),
:deep(.table-hover .table-secondary:hover > th) {
  background-color: #cfd3d7;
}
:deep(.table-success),
:deep(.table-success > th),
:deep(.table-success > td) {
  background-color: #c3e6cb;
}
:deep(.table-success th),
:deep(.table-success td),
:deep(.table-success thead th),
:deep(.table-success tbody + tbody) {
  border-color: #8fd19e;
}
:deep(.table-hover .table-success:hover) {
  background-color: #b1dfbb;
}
:deep(.table-hover .table-success:hover > td),
:deep(.table-hover .table-success:hover > th) {
  background-color: #b1dfbb;
}
:deep(.table-info),
:deep(.table-info > th),
:deep(.table-info > td) {
  background-color: #bee5eb;
}
:deep(.table-info th),
:deep(.table-info td),
:deep(.table-info thead th),
:deep(.table-info tbody + tbody) {
  border-color: #86cfda;
}
:deep(.table-hover .table-info:hover) {
  background-color: #abdde5;
}
:deep(.table-hover .table-info:hover > td),
:deep(.table-hover .table-info:hover > th) {
  background-color: #abdde5;
}
:deep(.table-warning),
:deep(.table-warning > th),
:deep(.table-warning > td) {
  background-color: #ffeeba;
}
:deep(.table-warning th),
:deep(.table-warning td),
:deep(.table-warning thead th),
:deep(.table-warning tbody + tbody) {
  border-color: #ffdf7e;
}
:deep(.table-hover .table-warning:hover) {
  background-color: #ffe8a1;
}
:deep(.table-hover .table-warning:hover > td),
:deep(.table-hover .table-warning:hover > th) {
  background-color: #ffe8a1;
}
:deep(.table-danger),
:deep(.table-danger > th),
:deep(.table-danger > td) {
  background-color: #f5c6cb;
}
:deep(.table-danger th),
:deep(.table-danger td),
:deep(.table-danger thead th),
:deep(.table-danger tbody + tbody) {
  border-color: #ed969e;
}
:deep(.table-hover .table-danger:hover) {
  background-color: #f1b0b7;
}
:deep(.table-hover .table-danger:hover > td),
:deep(.table-hover .table-danger:hover > th) {
  background-color: #f1b0b7;
}
:deep(.table-light),
:deep(.table-light > th),
:deep(.table-light > td) {
  background-color: #fcfcfc;
}
:deep(.table-light th),
:deep(.table-light td),
:deep(.table-light thead th),
:deep(.table-light tbody + tbody) {
  border-color: #f9fafa;
}
:deep(.table-hover .table-light:hover) {
  background-color: #efefef;
}
:deep(.table-hover .table-light:hover > td),
:deep(.table-hover .table-light:hover > th) {
  background-color: #efefef;
}
:deep(.table-dark),
:deep(.table-dark > th),
:deep(.table-dark > td) {
  background-color: #d1d5d7;
}
:deep(.table-dark th),
:deep(.table-dark td),
:deep(.table-dark thead th),
:deep(.table-dark tbody + tbody) {
  border-color: #aab0b5;
}
:deep(.table-hover .table-dark:hover) {
  background-color: #c3c9cb;
}
:deep(.table-hover .table-dark:hover > td),
:deep(.table-hover .table-dark:hover > th) {
  background-color: #c3c9cb;
}
:deep(.table-active),
:deep(.table-active > th),
:deep(.table-active > td) {
  background-color: rgba(55, 63, 67, 0.075);
}
:deep(.table-hover .table-active:hover) {
  background-color: rgba(44, 50, 53, 0.075);
}
:deep(.table-hover .table-active:hover > td),
:deep(.table-hover .table-active:hover > th) {
  background-color: rgba(44, 50, 53, 0.075);
}
:deep(.table .thead-dark th) {
  color: #fff;
  background-color: hsl(203, 10%, 40%);
  border-color: #6d7c85;
}
:deep(.table .thead-light th) {
  color: hsl(203, 10%, 48%);
  background-color: hsl(203, 10%, 88%);
  border-color: hsl(203, 10%, 80%);
}
:deep(.table-dark) {
  color: #fff;
  background-color: hsl(203, 10%, 40%);
}
:deep(.table-dark th),
:deep(.table-dark td),
:deep(.table-dark thead th) {
  border-color: #6d7c85;
}
:deep(.table-dark.table-bordered) {
  border: 0;
}
:deep(.table-dark.table-striped tbody tr:nth-of-type(odd)) {
  background-color: rgba(255, 255, 255, 0.05);
}
:deep(.table-dark.table-hover tbody tr:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.075);
}
@media (max-width: 575.98px) {
  :deep(.table-responsive-sm) {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  :deep(.table-responsive-sm > .table-bordered) {
    border: 0;
  }
}
@media (max-width: 767.98px) {
  :deep(.table-responsive-md) {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  :deep(.table-responsive-md > .table-bordered) {
    border: 0;
  }
}
@media (max-width: 991.98px) {
  :deep(.table-responsive-lg) {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  :deep(.table-responsive-lg > .table-bordered) {
    border: 0;
  }
}
@media (max-width: 1199.98px) {
  :deep(.table-responsive-xl) {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  :deep(.table-responsive-xl > .table-bordered) {
    border: 0;
  }
}
:deep(.table-responsive) {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
:deep(.table-responsive > .table-bordered) {
  border: 0;
}

:deep(.row) {
  display: flex;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}

:deep(.col-1) {
  flex: 0 0 8.33333333%;
  max-width: 8.33333333%;
}
:deep(.col-2) {
  flex: 0 0 16.66666667%;
  max-width: 16.66666667%;
}
:deep(.col-3) {
  flex: 0 0 25%;
  max-width: 25%;
}
:deep(.col-4) {
  flex: 0 0 33.33333333%;
  max-width: 33.33333333%;
}
:deep(.col-5) {
  flex: 0 0 41.66666667%;
  max-width: 41.66666667%;
}
:deep(.col-6) {
  flex: 0 0 50%;
  max-width: 50%;
}
:deep(.col-7) {
  flex: 0 0 58.33333333%;
  max-width: 58.33333333%;
}
:deep(.col-8) {
  flex: 0 0 66.66666667%;
  max-width: 66.66666667%;
}
:deep(.col-9) {
  flex: 0 0 75%;
  max-width: 75%;
}
:deep(.col-10) {
  flex: 0 0 83.33333333%;
  max-width: 83.33333333%;
}
:deep(.col-11) {
  flex: 0 0 91.66666667%;
  max-width: 91.66666667%;
}
:deep(.col-12) {
  flex: 0 0 100%;
  max-width: 100%;
}

:deep(.clearfix::after) {
  display: block;
  clear: both;
  content: "";
}

:deep(.float-left) {
  float: left !important;
}
:deep(.float-right) {
  float: right !important;
}
:deep(.float-none) {
  float: none !important;
}

:deep(.form-control),
:deep(.StripeElement) {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: hsl(203, 10%, 48%);
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid hsl(203, 10%, 72%);
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  :deep(.form-control),
  :deep(.StripeElement) {
    transition: none;
  }
}
:deep(.form-control::-ms-expand),
:deep(.StripeElement::-ms-expand) {
  background-color: transparent;
  border: 0;
}
:deep(.form-control:focus),
:deep(.StripeElement:focus) {
  color: hsl(203, 10%, 48%);
  background-color: #fff;
  border-color: #aed7f0;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(66, 162, 220, 0.25);
}
:deep(.form-control::-moz-placeholder),
:deep(.StripeElement::-moz-placeholder) {
  color: hsl(203, 10%, 56%);
  opacity: 1;
}
:deep(.form-control::placeholder),
:deep(.StripeElement::placeholder) {
  color: hsl(203, 10%, 56%);
  opacity: 1;
}
:deep(.form-control:disabled),
:deep(.StripeElement:disabled),
:deep(.form-control[readonly],
[readonly].StripeElement) {
  background-color: hsl(203, 10%, 88%);
  opacity: 1;
}
:deep(input[type="date"].form-control),
:deep(input[type="date"].StripeElement),
:deep(input[type="time"].form-control),
:deep(input[type="time"].StripeElement),
:deep(input[type="datetime-local"].form-control),
:deep(input[type="datetime-local"].StripeElement),
:deep(input[type="month"].form-control),
:deep(input[type="month"].StripeElement) {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
:deep(select.form-control:-moz-focusring,
select.StripeElement:-moz-focusring) {
  color: transparent;
  text-shadow: 0 0 0 hsl(203, 10%, 48%);
}
:deep(select.form-control:focus::-ms-value,
select.StripeElement:focus::-ms-value) {
  color: hsl(203, 10%, 48%);
  background-color: #fff;
}
:deep(.form-control-file),
:deep(.form-control-range) {
  display: block;
  width: 100%;
}
:deep(.col-form-label) {
  padding-top: calc(0.375rem + 1px);
  padding-bottom: calc(0.375rem + 1px);
  margin-bottom: 0;
  font-size: inherit;
  line-height: 1.5;
}
:deep(.col-form-label-lg) {
  padding-top: calc(0.5rem + 1px);
  padding-bottom: calc(0.5rem + 1px);
  font-size: 1.25rem;
  line-height: 1.5;
}
:deep(.col-form-label-sm) {
  padding-top: calc(0.25rem + 1px);
  padding-bottom: calc(0.25rem + 1px);
  font-size: 0.875rem;
  line-height: 1.5;
}
:deep(.form-control-plaintext) {
  display: block;
  width: 100%;
  padding: 0.375rem 0;
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: hsl(203, 10%, 32%);
  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0;
}
:deep(.form-control-plaintext.form-control-sm),
:deep(.form-control-plaintext.form-control-lg) {
  padding-right: 0;
  padding-left: 0;
}
:deep(.form-control-sm) {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}
:deep(.form-control-lg) {
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  font-size: 1.25rem;
  line-height: 1.5;
  border-radius: 0.3rem;
}
:deep(select.form-control[size],
select[size].StripeElement,
select.form-control[multiple],
select[multiple].StripeElement) {
  height: auto;
}
:deep(textarea.form-control),
:deep(textarea.StripeElement) {
  height: auto;
}
:deep(.form-group) {
  margin-bottom: 1rem;
}
:deep(.form-text) {
  display: block;
  margin-top: 0.25rem;
}
:deep(.form-row) {
  display: flex;
  flex-wrap: wrap;
  margin-right: -5px;
  margin-left: -5px;
}
:deep(.form-row > .col),
:deep(.form-row > [class*="col-"]) {
  padding-right: 5px;
  padding-left: 5px;
}
:deep(.form-check) {
  position: relative;
  display: block;
  padding-left: 1.25rem;
}
:deep(.form-check-input) {
  position: absolute;
  margin-top: 0.3rem;
  margin-left: -1.25rem;
}
:deep(.form-check-input[disabled] ~ .form-check-label),
:deep(.form-check-input:disabled ~ .form-check-label) {
  color: hsl(203, 10%, 56%);
}
:deep(.form-check-label) {
  margin-bottom: 0;
}
:deep(.form-check-inline) {
  display: inline-flex;
  align-items: center;
  padding-left: 0;
  margin-right: 0.75rem;
}
:deep(.form-check-inline .form-check-input) {
  position: static;
  margin-top: 0;
  margin-right: 0.3125rem;
  margin-left: 0;
}
:deep(.valid-feedback) {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #28a745;
}
:deep(.valid-tooltip) {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 5;
  display: none;
  max-width: 100%;
  padding: 0.25rem 0.5rem;
  margin-top: 0.1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #fff;
  background-color: rgba(40, 167, 69, 0.9);
  border-radius: 0.25rem;
}
:deep(.form-row > .col > .valid-tooltip),
:deep(.form-row > [class*="col-"] > .valid-tooltip) {
  left: 5px;
}
:deep(.was-validated :valid ~ .valid-feedback),
:deep(.was-validated :valid ~ .valid-tooltip),
:deep(.is-valid ~ .valid-feedback),
:deep(.is-valid ~ .valid-tooltip) {
  display: block;
}
:deep(.was-validated .form-control:valid),
:deep(.was-validated .StripeElement:valid),
:deep(.form-control.is-valid),
:deep(.is-valid.StripeElement) {
  border-color: #28a745;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
:deep(.was-validated .form-control:valid:focus),
:deep(.was-validated .StripeElement:valid:focus),
:deep(.form-control.is-valid:focus),
:deep(.is-valid.StripeElement:focus) {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}
:deep(.was-validated select.form-control:valid),
:deep(.was-validated select.StripeElement:valid),
:deep(select.form-control.is-valid),
:deep(select.is-valid.StripeElement) {
  padding-right: 3rem !important;
  background-position: right 1.5rem center;
}
:deep(.was-validated textarea.form-control:valid),
:deep(.was-validated textarea.StripeElement:valid),
:deep(textarea.form-control.is-valid),
:deep(textarea.is-valid.StripeElement) {
  padding-right: calc(1.5em + 0.75rem);
  background-position: top calc(0.375em + 0.1875rem) right calc(0.375em + 0.1875rem);
}
:deep(.was-validated .custom-select:valid),
:deep(.custom-select.is-valid) {
  border-color: #28a745;
  padding-right: calc(0.75em + 2.3125rem) !important;
  background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='hsl%28203, 10%, 40%%29' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")
      right 0.75rem center/8px 10px no-repeat,
    #fff
      url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e")
      center right 1.75rem / calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) no-repeat;
}
:deep(.was-validated .custom-select:valid:focus),
:deep(.custom-select.is-valid:focus) {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}
:deep(.was-validated .form-check-input:valid ~ .form-check-label),
:deep(.form-check-input.is-valid ~ .form-check-label) {
  color: #28a745;
}
:deep(.was-validated .form-check-input:valid ~ .valid-feedback),
:deep(.was-validated .form-check-input:valid ~ .valid-tooltip),
:deep(.form-check-input.is-valid ~ .valid-feedback),
:deep(.form-check-input.is-valid ~ .valid-tooltip) {
  display: block;
}
:deep(.was-validated .custom-control-input:valid ~ .custom-control-label),
:deep(.custom-control-input.is-valid ~ .custom-control-label) {
  color: #28a745;
}
:deep(.was-validated .custom-control-input:valid ~ .custom-control-label::before),
:deep(.custom-control-input.is-valid ~ .custom-control-label::before) {
  border-color: #28a745;
}
:deep(.was-validated .custom-control-input:valid:checked ~ .custom-control-label::before),
:deep(.custom-control-input.is-valid:checked ~ .custom-control-label::before) {
  border-color: #34ce57;
  background-color: #34ce57;
}
:deep(.was-validated .custom-control-input:valid:focus ~ .custom-control-label::before),
:deep(.custom-control-input.is-valid:focus ~ .custom-control-label::before) {
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}
:deep(.was-validated .custom-control-input:valid:focus:not(:checked) ~ .custom-control-label::before),
:deep(.custom-control-input.is-valid:focus:not(:checked) ~ .custom-control-label::before) {
  border-color: #28a745;
}
:deep(.was-validated .custom-file-input:valid ~ .custom-file-label),
:deep(.custom-file-input.is-valid ~ .custom-file-label) {
  border-color: #28a745;
}
:deep(.was-validated .custom-file-input:valid:focus ~ .custom-file-label),
:deep(.custom-file-input.is-valid:focus ~ .custom-file-label) {
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}
:deep(.invalid-feedback) {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
:deep(.invalid-tooltip) {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 5;
  display: none;
  max-width: 100%;
  padding: 0.25rem 0.5rem;
  margin-top: 0.1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #fff;
  background-color: rgba(220, 53, 69, 0.9);
  border-radius: 0.25rem;
}
:deep(.form-row > .col > .invalid-tooltip),
:deep(.form-row > [class*="col-"] > .invalid-tooltip) {
  left: 5px;
}
:deep(.was-validated :invalid ~ .invalid-feedback),
:deep(.was-validated :invalid ~ .invalid-tooltip),
:deep(.is-invalid ~ .invalid-feedback),
:deep(.is-invalid ~ .invalid-tooltip) {
  display: block;
}
:deep(.was-validated .form-control:invalid),
:deep(.was-validated .StripeElement:invalid),
:deep(.form-control.is-invalid),
:deep(.is-invalid.StripeElement) {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}
:deep(.was-validated .form-control:invalid:focus),
:deep(.was-validated .StripeElement:invalid:focus),
:deep(.form-control.is-invalid:focus),
:deep(.is-invalid.StripeElement:focus) {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
:deep(.was-validated select.form-control:invalid),
:deep(.was-validated select.StripeElement:invalid),
:deep(select.form-control.is-invalid),
:deep(select.is-invalid.StripeElement) {
  padding-right: 3rem !important;
  background-position: right 1.5rem center;
}
:deep(.was-validated textarea.form-control:invalid),
:deep(.was-validated textarea.StripeElement:invalid),
:deep(textarea.form-control.is-invalid),
:deep(textarea.is-invalid.StripeElement) {
  padding-right: calc(1.5em + 0.75rem);
  background-position: top calc(0.375em + 0.1875rem) right calc(0.375em + 0.1875rem);
}
:deep(.was-validated .custom-select:invalid),
:deep(.custom-select.is-invalid) {
  border-color: #dc3545;
  padding-right: calc(0.75em + 2.3125rem) !important;
  background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='hsl%28203, 10%, 40%%29' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")
      right 0.75rem center/8px 10px no-repeat,
    #fff
      url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e")
      center right 1.75rem / calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) no-repeat;
}
:deep(.was-validated .custom-select:invalid:focus),
:deep(.custom-select.is-invalid:focus) {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
:deep(.was-validated .form-check-input:invalid ~ .form-check-label),
:deep(.form-check-input.is-invalid ~ .form-check-label) {
  color: #dc3545;
}
:deep(.was-validated .form-check-input:invalid ~ .invalid-feedback),
:deep(.was-validated .form-check-input:invalid ~ .invalid-tooltip),
:deep(.form-check-input.is-invalid ~ .invalid-feedback),
:deep(.form-check-input.is-invalid ~ .invalid-tooltip) {
  display: block;
}
:deep(.was-validated .custom-control-input:invalid ~ .custom-control-label),
:deep(.custom-control-input.is-invalid ~ .custom-control-label) {
  color: #dc3545;
}
:deep(.was-validated .custom-control-input:invalid ~ .custom-control-label::before),
:deep(.custom-control-input.is-invalid ~ .custom-control-label::before) {
  border-color: #dc3545;
}
:deep(.was-validated .custom-control-input:invalid:checked ~ .custom-control-label::before),
:deep(.custom-control-input.is-invalid:checked ~ .custom-control-label::before) {
  border-color: #e4606d;
  background-color: #e4606d;
}
:deep(.was-validated .custom-control-input:invalid:focus ~ .custom-control-label::before),
:deep(.custom-control-input.is-invalid:focus ~ .custom-control-label::before) {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
:deep(.was-validated .custom-control-input:invalid:focus:not(:checked) ~ .custom-control-label::before),
:deep(.custom-control-input.is-invalid:focus:not(:checked) ~ .custom-control-label::before) {
  border-color: #dc3545;
}
:deep(.was-validated .custom-file-input:invalid ~ .custom-file-label),
:deep(.custom-file-input.is-invalid ~ .custom-file-label) {
  border-color: #dc3545;
}
:deep(.was-validated .custom-file-input:invalid:focus ~ .custom-file-label),
:deep(.custom-file-input.is-invalid:focus ~ .custom-file-label) {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
:deep(.form-inline) {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
}
:deep(.form-inline .form-check) {
  width: 100%;
}
@media (min-width: 576px) {
  :deep(.form-inline label) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
  }
  :deep(.form-inline .form-group) {
    display: flex;
    flex: 0 0 auto;
    flex-flow: row wrap;
    align-items: center;
    margin-bottom: 0;
  }
  :deep(.form-inline .form-control),
  :deep(.form-inline .StripeElement) {
    display: inline-block;
    width: auto;
    vertical-align: middle;
  }
  :deep(.form-inline .form-control-plaintext) {
    display: inline-block;
  }
  :deep(.form-inline .input-group),
  :deep(.form-inline .custom-select) {
    width: auto;
  }
  :deep(.form-inline .form-check) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    padding-left: 0;
  }
  :deep(.form-inline .form-check-input) {
    position: relative;
    flex-shrink: 0;
    margin-top: 0;
    margin-right: 0.25rem;
    margin-left: 0;
  }
  :deep(.form-inline .custom-control) {
    align-items: center;
    justify-content: center;
  }
  :deep(.form-inline .custom-control-label) {
    margin-bottom: 0;
  }
}

</style>
